import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, combineLatestWith, map, Subscription, throwError } from 'rxjs';

import { CryptoService } from './crypto.service';
import { Survey, SurveyService } from './survey.service';
import { ZipService } from './zip.service';

interface SubmittedFile {
    file: File;
    survey?: Survey;
};

interface Start {
    key: string;
    keyId: string;
}

export type SubmissionStatus = 'nope' | 'loading' | 'ready' | 'survey' | 'review' | 'sending' | 'complete';


@Injectable({
    providedIn: 'root'
})
export class SubmissionService implements OnDestroy {
    private submissions = new BehaviorSubject<SubmittedFile[]>([]);
    private status = new BehaviorSubject<SubmissionStatus>('loading');

    private key?: string;
    private keyId?: string;

    subscription: Subscription;
    files$ = this.submissions.pipe(
        combineLatestWith(this.surveyService.surveyFiles$),
        map(([submissions, surveyFiles]) => [
            ...submissions.flatMap(s => [s.file]),
            ...surveyFiles])
    );
    status$ = this.status.asObservable();

    constructor(
        private surveyService: SurveyService,
        private zipService: ZipService,
        private cryptoService: CryptoService,
        private http: HttpClient) {
        this.subscription = this.surveyService.incompleteSurveys$.subscribe(
            s => {
                if (s.length === 0 && this.started()) {
                    this.status.next('review');
                }
            }
        )
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    addFiles(files: File[]) {
        const submissions = files.map(file => ({
            file,
            // survey: <Survey>{
            //     // add a survey for each file
            //     id: file.name + '_survey',
            //     type: 'file',
            //     complete: false,
            //     describesFile: file
            // }
        }));

        this.submissions.next(
            [...this.submissions.value,
            ...submissions]);

        if (this.surveyService.entry()) {
            // this.surveyService.addSurveys(submissions.map(s => s.survey));
            this.status.next('survey');
        }
    }

    deleteFile(file: File) {
        this.surveyService.deleteFileSurveys(file);
        this.submissions.next(
            this.submissions.value.filter(
                s => s.file !== file
            ));
    }

    start() {
        if (this.status.value !== 'loading') {
            // already started
            return;
        }

        // This call is executed on the server and in the browser.
        this.http
            .get<Start>(`/api/start/`)
            .pipe(
                catchError(error => {
                    this.status.next('nope');
                    console.error(error);
                    return throwError(() => new Error('Encountered an error during the key retrieval.'));
                })
            )
            .subscribe((start) => {
                this.setKey(start.key, start.keyId);
                this.status.next('ready');
            });
    }

    async submit(files: File[]) {
        if (!files || !this.started()) {
            return;
        }

        this.status.next('sending');
        // TODO: if survey filled in, generate file
        const zip = await this.zipService.zip(files);

        const formData = new FormData();
        if (this.encryptionActivated()) {
            if (!this.key || !this.keyId) {
                throw new Error('Key not set');
            }
            // In an AES-encrypted ZIP-file, only the contents are encrypted.
            // The filenames are in plaintext!
            // Encrypt the entire ZIP-file instead to hide everything:
            const pkg = await this.cryptoService.package(zip, this.key);

            formData.append('key_id', this.keyId); // identify the key which was used
            formData.append('blob', new Blob([pkg.data], { type: "application/octet-stream" }));
            formData.append('secret', pkg.secret);
            formData.append('checksum', await this.cryptoService.checksum(pkg.data));
        } else {
            formData.append('email', this.surveyService.email);
            // Encryption is disabled
            formData.append('blob', new Blob([zip], { type: "application/zip" }));
            formData.append('checksum', await this.cryptoService.checksum(zip));
        }

        /**
         * Give the user some time to see the progress
         */
        let notTooQuick = new Promise<void>((resolve) => {
            setTimeout(resolve, 5000)
        });

        return new Promise<void>((resolve) => {
            this.subscription.add(
                this.http.post(
                    '/api/submit/',
                    formData).subscribe(async () => {
                        if (this.encryptionActivated()) await notTooQuick;
                        this.status.next('complete');
                        resolve();
                    }));
        });

    }

    public setStatus(status: SubmissionStatus) {
        this.status.next(status);
    }

    /**
     * The public key used for encrypting the submission.
     * @param key
     * @param keyId
     */
    private setKey(key?: string, keyId?: string) {
        this.key = key;
        this.keyId = keyId;
    }

    public encryptionActivated(): boolean {
        return !!this.key;
    }

    public started(): boolean {
        return this.status.value !== 'loading';
    }
}
